import React, { useEffect } from 'react';
import range from 'lodash/range';
import { Form, Field } from 'react-final-form';
import { Box } from '@/shared/ui/box';
import { Image } from '@/shared/ui/image';
import { FormField } from '@/shared/ui/form/form-field';
import { SMSInput } from '@/shared/ui/form/sms-input';
import { Link } from '@/shared/ui/link';
import {
  BodyTextBoldPrimary,
  BodyTextRegularSecondary,
  BodyTextBoldSecondary,
} from '@/shared/ui/typography';
import { Timer } from '@/shared/ui/timer';
import { useAuth } from '@/features/auth-managment/model/auth-context';
import {
  FormWrapper,
  SpinnerBox,
  CircleBorder,
  CircleCore,
  CircleImage,
  ConfirmationFormWrapper,
  ConfirmationPhoneText,
  AuthCloseIconImage,
  FormContainer,
  ButtonContinue,
} from '@/features/auth-managment/styles';

export const CreatePasswordSms = () => {
  const {
    onSubmit,
    spinner,
    t,
    validateCreatePassword,
    handleClose,
    currentCountry,
    currentStep,
    currentPhone,
    setCurrentStep,
    handleSmsResend,
    inputRefs,
    handleResetPassword,
    handleKeyDown,
    handleDigitChange,
    digits,
    setDigits,
  } = useAuth();

  const handleFormSubmit = (values) => {
    const code = digits.join('');
    return onSubmit({ ...values, code });
  };

  useEffect(() => {
    setDigits(Array(6).fill(''));
  }, []);

  return (
    <FormWrapper $currentStep={currentStep}>
      <FormContainer>
        <Box onClick={handleClose}>
          <AuthCloseIconImage
            width={32}
            height={32}
            src="/images/icons/close-dialog.svg"
          />
        </Box>

        <SpinnerBox>
          <CircleBorder>
            <CircleCore />
          </CircleBorder>
          <CircleImage
            width={56}
            height={56}
            src={'/images/background/chat-spin.svg'}
          />
        </SpinnerBox>

        <Box $paddingTop={16} $paddingBottom={16} $textAlign="center">
          <BodyTextRegularSecondary color="#AFAFAF">
            {t('auth.sms.desc')}
          </BodyTextRegularSecondary>
        </Box>

        <ConfirmationPhoneText>
          <BodyTextBoldPrimary>
            {currentCountry.code || user?.phone?.code}
            {currentPhone || user?.phone?.value}
          </BodyTextBoldPrimary>
          <span onClick={() => setCurrentStep('authorization')}>
            <Image src="/images/icons/pencil.svg" width={20} height={20} />
          </span>
        </ConfirmationPhoneText>

        <Box>
          <BodyTextBoldSecondary>{t('auth.sms.code')}</BodyTextBoldSecondary>
          <ConfirmationFormWrapper>
            {range(6).map((index) => (
              <SMSInput
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                index={index}
                value={digits?.[index]}
                onChange={handleDigitChange}
                onKeyDown={handleKeyDown}
              />
            ))}
          </ConfirmationFormWrapper>
        </Box>

        <Form
          validate={validateCreatePassword}
          onSubmit={handleFormSubmit}
          render={({ handleSubmit, submitting, pristine }) => (
            <form onSubmit={handleSubmit}>
              <Box $paddingTop={16}>
                <Field
                  name="password"
                  component={FormField}
                  type="password"
                  placeholder={t('auth.login.password.placeholder')}
                  label={
                    <BodyTextBoldSecondary>
                      {t('auth.recover.password.new')}
                    </BodyTextBoldSecondary>
                  }
                />
              </Box>
              <Box $paddingTop={16}>
                <Field
                  name="confirmPassword"
                  component={FormField}
                  type="password"
                  placeholder={t('auth.login.password.placeholder')}
                  label={
                    <BodyTextBoldSecondary>
                      {t('auth.recover.password.confirm')}
                    </BodyTextBoldSecondary>
                  }
                />
              </Box>
              <Box $paddingTop={24}>
                <ButtonContinue
                  type="submit"
                  disabled={submitting}
                  isLoading={spinner}
                >
                  <BodyTextBoldSecondary>
                    {t('auth.recover.submit')}
                  </BodyTextBoldSecondary>
                </ButtonContinue>
              </Box>
            </form>
          )}
        />

        <Box
          $paddingTop={16}
          $paddingBottom={16}
          $display="flex"
          $alignItems="center"
          $gap={8}
        >
          <Box $cursor="pointer" onClick={handleResetPassword}>
            <BodyTextRegularSecondary color="#AFAFAF">
              {t('auth.sms.resend_question')}
            </BodyTextRegularSecondary>
          </Box>

          <Timer
            handleResend={handleSmsResend}
            triggerSlot={<Link href="#">{t('auth.sms.resend')}</Link>}
          />
        </Box>
      </FormContainer>
    </FormWrapper>
  );
};
